import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { NavLink, useLocation } from "react-router-dom";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import CustomScrollbars from "../../../utils/CustomScrollbars";
import IntlMessages from "../../../utils/IntlMessages";
import { userSignOut } from "../../../actions";
import { translator } from "../../../utils/Translator";

const MenuList = ({ onToggleCollapsed, data, loading }) => {
  const dispatch = useDispatch();
  const { translate } = useSelector(({ i18n }) => i18n);
  const location = useLocation();

  const [menuTree, setMenuTree] = useState([]);

  // กลุ่มเมนูที่กางอยู่ เก็บเป็น state ไม่ใช่ class บน DOM
  // เพราะ Drawer แบบ temporary จะ unmount MenuList ทิ้งตอนปิด → class "open" หายไปด้วย
  const [openMenus, setOpenMenus] = useState({});

  // 🧩 แปลง list เป็น tree structure
  useEffect(() => {
    if (data && data.length > 0) {
      const map = {};
      const roots = [];

      data.forEach((item) => {
        map[item.Menu_ID] = { ...item, Children: [] };
      });

      data.forEach((item) => {
        if (item.Menu_PARENT && map[item.Menu_PARENT]) {
          map[item.Menu_PARENT].Children.push(map[item.Menu_ID]);
        } else if (!item.Menu_PARENT) {
          roots.push(map[item.Menu_ID]);
        }
      });

      // ✅ เรียงลำดับตาม Menu_SORT
      roots.sort((a, b) => a.Menu_SORT - b.Menu_SORT);
      roots.forEach((r) => r.Children.sort((a, b) => a.Menu_SORT - b.Menu_SORT));

      setMenuTree(roots);
    }
  }, [data]);

  // หากลุ่มที่มีเมนูย่อยตรงกับ path ปัจจุบัน
  const activeParentId = useMemo(() => {
    const currentPath = location.pathname;
    const isMatch = (path) =>
      !!path && (currentPath === path || currentPath.startsWith(`${path}/`));

    const parent = menuTree.find(
      (item) =>
        item.Children &&
        item.Children.length > 0 &&
        item.Children.some((child) => isMatch(child.Path))
    );

    return parent ? parent.Menu_ID : null;
  }, [menuTree, location.pathname]);

  // กางกลุ่มของเมนูที่เปิดใช้งานอยู่ให้อัตโนมัติ
  // ทำงานทั้งตอนเปลี่ยนหน้า และตอน MenuList ถูก mount ใหม่หลังเปิด Drawer
  // ผู้ใช้จึงเห็นเมนูที่เปิดอยู่ทันที ไม่ต้องกดหัวข้อซ้ำเพื่อไปหา
  useEffect(() => {
    if (activeParentId == null) return;
    setOpenMenus((prev) =>
      prev[activeParentId] ? prev : { ...prev, [activeParentId]: true }
    );
  }, [activeParentId]);

  const handleSignout = () => {
    dispatch(userSignOut());
  };

  const toggleMenu = (menuId) => {
    setOpenMenus((prev) => ({ ...prev, [menuId]: !prev[menuId] }));
  };

  const renderMenu = (items) => {
    return items.map((item) => {
      // ✅ ถ้ามีลูก ให้เป็น collapse-box
      if (item.Children && item.Children.length > 0) {
        const isOpen = !!openMenus[item.Menu_ID];

        return (
          <li
            className={`menu collapse-box${isOpen ? " open" : ""}`}
            key={item.Menu_ID}
          >
            <Button onClick={() => toggleMenu(item.Menu_ID)}>
              <i className={item.Icon || "zmdi zmdi-folder"} />
              <span className="nav-text">
                {translator(translate[item.LabelKey], item.Name)}
              </span>
            </Button>
            <ul className="sub-menu">
              {item.Children.map((child) => (
                <li key={child.Menu_ID} style={{ position: 'relative' }}>
                  {child.NotificationCount > 0 && (
                    <span
                      className="menu-badge"
                      style={{
                        position: 'absolute',
                        left: '24px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        backgroundColor: '#ff6f00',
                        color: '#fff',
                        fontWeight: 'bold',
                        borderRadius: '50%',
                        minWidth: '22px',
                        height: '22px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '9px',
                        padding: '0 6px',
                        zIndex: 1
                      }}
                    >
                      {child.NotificationCount > 999 ? '999+' : child.NotificationCount}
                    </span>
                  )}
                  <NavLink
                    className="prepend-icon"
                    to={{ pathname: child.Path, state: { menuId: child.Menu_ID } }}
                    onClick={onToggleCollapsed}
                  >
                    <span>{translator(translate[child.LabelKey], child.Name)}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </li>
        );
      }

      // ✅ ถ้าไม่มีลูก เป็นเมนูปกติ
      return (
        <li className="menu no-arrow" key={item.Menu_ID}>
          <NavLink
            to={{ pathname: item.Path, state: { menuId: item.Menu_ID } }}
            onClick={onToggleCollapsed}
          >
            <i className={item.Icon || "zmdi zmdi-dot-circle"} />
            <span className="nav-text">
              {translator(translate[item.LabelKey], item.Name)}
            </span>
          </NavLink>
        </li>
      );
    });
  };

  return (
    <CustomScrollbars className="scrollbar">
      <ul className="nav-menu">
        <li className="nav-header">
          <IntlMessages id="sidebar.main" />
        </li>

        {loading && (
          <div className="w-100 text-center">
            <CircularProgress />
          </div>
        )}

        {!loading && renderMenu(menuTree)}

        {!loading && (
          <li className="menu no-arrow">
            <NavLink to="#" onClick={() => handleSignout()}>
              <i className="zmdi zmdi-sign-in zmdi-hc-fw" />
              <span className="nav-text">
                {translator(translate.ISA_Logoff, "ออกจากระบบ")}
              </span>
            </NavLink>
          </li>
        )}
      </ul>
    </CustomScrollbars>
  );
};

export default MenuList;
